import _ from 'lodash';
import { useCallback, useMemo } from 'react';

import { WrappedPair } from '@/entities/WrappedPair';
import { useAppSelector } from '@/hooks';
import { HISTORY_RANGE } from './constants';
import { FundingHistory, LiquidityHistory, OrderHistory, TradeHistory, TransferHistory } from './types';

import { DAPP_CHAIN_CONFIGS } from '@/constants/chain';
import { QUERY_KEYS } from '@/constants/query';
import { queryClient } from '@/global';
import { isNativeTokenAddr } from '@/utils/token';
import { NATIVE_TOKEN_ADDRESS } from '@synfutures/sdks-perp';
import { useMarginTokenInfoMap } from '../chain/hook';
import {
  useInfiniteAccountBalanceHistoryQuery,
  useInfiniteFundingHistoryQuery,
  useInfiniteLiquidityHistoryQuery,
  useInfiniteOrdersHistoryQuery,
  useInfiniteTransferHistoryQuery,
  useInfiniteVirtualTradeHistoryQuery,
} from './query';
import { selectHistoryRange } from './slice';

export function useInfiniteVirtualTradeHistory({
  chainId,
  userAddr,
  pair = 'all',
  timeRange = HISTORY_RANGE.ALL,
}: {
  chainId: number | undefined;
  userAddr: string | undefined;
  pair?: WrappedPair | 'all';
  timeRange?: HISTORY_RANGE;
}) {
  const args = useMemo(
    () => ({
      chainId,
      userAddress: userAddr,
      timeRange,
      ...(pair !== 'all' && {
        instrumentAddress: pair.metaPair.instrumentId,
        expiry: pair.expiry,
      }),
    }),
    [chainId, pair, userAddr, timeRange],
  );
  const queryState = useInfiniteVirtualTradeHistoryQuery(args);
  // const { data: pairBasicInfos } = useAllPairBasicInfoByGraph(chainId);
  const refreshData = useCallback(() => {
    queryClient.resetQueries({
      queryKey: QUERY_KEYS.GRAPH.VIRTUAL_TRADE_HISTORY(args),
    });
  }, [args]);

  const histories: TradeHistory[] = useMemo(() => {
    const { data } = queryState;
    const tradeHistoryListOrigin = _.uniqBy(data?.pages.flat(), 'id');

    return tradeHistoryListOrigin || [];
  }, [queryState]);

  return {
    queryState,
    histories,
    refreshData,
  };
}

export function useInfiniteOrdersHistory({
  chainId,
  userAddr,
  pair = 'all',
  timeRange = HISTORY_RANGE.ALL,
}: {
  chainId: number | undefined;
  userAddr: string | undefined;
  pair?: WrappedPair | 'all';
  timeRange?: HISTORY_RANGE;
}) {
  const args = useMemo(
    () => ({
      chainId,
      userAddress: userAddr,
      timeRange,
      ...(pair !== 'all' && {
        instrumentAddress: pair.metaPair.instrumentId,
        expiry: pair.expiry,
      }),
    }),
    [chainId, pair, userAddr, timeRange],
  );

  const queryState = useInfiniteOrdersHistoryQuery(args);

  const refreshData = useCallback(() => {
    queryClient.resetQueries({
      queryKey: QUERY_KEYS.GRAPH.ORDERS_HISTORY(args),
    });
  }, [args]);

  const histories: OrderHistory[] = useMemo(() => {
    const { data } = queryState;
    const orderHistoryListOrigin = data?.pages.flat();
    return orderHistoryListOrigin || [];
  }, [queryState]);

  return {
    queryState,
    histories: _.uniqBy(_.orderBy(histories, ['placeTimestamp', 'timestamp'], ['desc', 'desc']), 'id'),
    refreshData,
  };
}

export function useInfiniteFundingHistory({
  chainId,
  userAddr,
  pair = 'all',
  timeRange = HISTORY_RANGE.ALL,
}: {
  chainId: number | undefined;
  userAddr: string | undefined;
  pair?: WrappedPair | 'all';
  timeRange?: HISTORY_RANGE;
}) {
  const args = useMemo(
    () => ({
      chainId,
      userAddress: userAddr,
      timeRange,
      ...(pair !== 'all' && {
        instrumentAddress: pair.metaPair.instrumentId,
        expiry: pair.expiry,
      }),
    }),
    [chainId, pair, userAddr, timeRange],
  );

  const queryState = useInfiniteFundingHistoryQuery(args);

  const refreshData = useCallback(() => {
    queryClient.resetQueries({
      queryKey: QUERY_KEYS.GRAPH.FUNDING_HISTORY(args),
    });
  }, [args]);

  const histories: FundingHistory[] = useMemo(() => {
    const fundingHistoryListOrigin = queryState.data?.pages.flat() || [];
    return fundingHistoryListOrigin;
  }, [queryState.data]);

  return {
    queryState,
    histories: _.uniqBy(_.orderBy(histories, ['timestamp'], ['desc']), 'id'),
    refreshData,
  };
}

export function useInfiniteTransferHistory({
  chainId,
  userAddr,
  pair = 'all',
  timeRange = HISTORY_RANGE.ALL,
}: {
  chainId: number | undefined;
  userAddr: string | undefined;
  pair?: WrappedPair | 'all';
  timeRange?: HISTORY_RANGE;
}) {
  const args = useMemo(
    () => ({
      chainId,
      userAddress: userAddr,
      timeRange,
      ...(pair !== 'all' && {
        instrumentAddress: pair.metaPair.instrumentId,
        expiry: pair.expiry,
      }),
    }),
    [chainId, pair, userAddr, timeRange],
  );

  const queryState = useInfiniteTransferHistoryQuery(args);

  const refreshData = useCallback(() => {
    queryClient.resetQueries({
      queryKey: QUERY_KEYS.GRAPH.TRANSFERS_HISTORY(args),
    });
  }, [args]);

  const histories: TransferHistory[] = useMemo(() => {
    const transferHistoryListOrigin = queryState.data?.pages.flat() || [];
    return transferHistoryListOrigin;
  }, [queryState.data]);

  return {
    queryState,
    histories: _.uniqBy(_.orderBy(histories, ['timestamp'], ['desc']), 'id'),
    refreshData,
  };
}

export function useInfiniteLiquidityHistory({
  chainId,
  userAddr,
  pair = 'all',
  timeRange = HISTORY_RANGE.ALL,
}: {
  chainId: number | undefined;
  userAddr: string | undefined;
  pair?: WrappedPair | 'all';
  timeRange?: HISTORY_RANGE;
}) {
  const args = useMemo(
    () => ({
      chainId,
      userAddress: userAddr,
      timeRange,
      ...(pair !== 'all' && {
        instrumentAddress: pair.metaPair.instrumentId,
        expiry: pair.expiry,
      }),
    }),
    [chainId, pair, userAddr, timeRange],
  );

  const queryState = useInfiniteLiquidityHistoryQuery(args);

  const refreshData = useCallback(() => {
    queryClient.resetQueries({
      queryKey: QUERY_KEYS.GRAPH.LIQUIDITY_HISTORY(args),
    });
  }, [args]);

  const histories: LiquidityHistory[] = useMemo(() => {
    const liquidityHistoryListOrigin = queryState.data?.pages.flat() || [];
    return liquidityHistoryListOrigin;
  }, [queryState.data]);

  return {
    queryState,
    histories: _.uniqBy(_.orderBy(histories, ['timestamp'], ['desc']), 'id'),
    refreshData,
  };
}

export function useInfiniteAccountBalanceHistory({
  chainId,
  userAddr,
  timeRange = HISTORY_RANGE.ALL,
}: {
  chainId: number | undefined;
  userAddr: string | undefined;
  timeRange?: HISTORY_RANGE;
}) {
  const args = useMemo(
    () => ({
      chainId,
      userAddress: userAddr,
      timeRange,
    }),
    [chainId, userAddr, timeRange],
  );
  const marginTokenMap = useMarginTokenInfoMap(chainId);
  const queryState = useInfiniteAccountBalanceHistoryQuery(args);

  const histories = useMemo(() => {
    const chainConfig = chainId && DAPP_CHAIN_CONFIGS[chainId];

    return (
      (chainConfig &&
        marginTokenMap &&
        _.orderBy(queryState.data?.pages.flat() || [], ['timestamp'], ['desc']).map((history) => {
          const quoteAddr = history.tokenAddress.toLowerCase();
          let quote = marginTokenMap[quoteAddr];
          if (
            (quoteAddr === NATIVE_TOKEN_ADDRESS.toLowerCase() || isNativeTokenAddr(quoteAddr)) &&
            chainConfig.wrappedNativeToken
          ) {
            quote = chainConfig.wrappedNativeToken;
          }
          return {
            ...history,
            timestamp: history.timestamp,
            // trader: event.args.trader,
            quoteAddr: quoteAddr,
            quote: quote,
            logIndex: 0,
            // amount: BigNumber.from(event.args.quantity),
            type: history.typeString,
            txHash: history.txHash,
            chainId,
          };
        })) ||
      []
    );
  }, [chainId, marginTokenMap, queryState.data]);
  const refreshData = useCallback(() => {
    queryClient.resetQueries({
      queryKey: QUERY_KEYS.GRAPH.ACCOUNT_BALANCE_HISTORY(args),
    });
  }, [args]);
  return {
    queryState,
    histories,
    refreshData,
  };
}

export function useHistoryRange(): HISTORY_RANGE {
  return useAppSelector(selectHistoryRange);
}
